import React, { useEffect } from 'react';
import '../styles/lead.css';

class FormSubmitter {
    private form: HTMLFormElement;
    private submitButton: HTMLButtonElement;
    private endpoint: string;

    constructor(formId: string, submitButtonSelector: string, endpoint: string) {
        this.form = document.getElementById(formId) as HTMLFormElement;
        this.submitButton = document.querySelector(submitButtonSelector) as HTMLButtonElement;
        this.endpoint = endpoint;
        this.initialize();
    }

    private initialize() {
        this.submitButton.addEventListener('click', (e) => this.handleSubmit(e));
    }

    private async handleSubmit(e: Event) {
        e.preventDefault();
        
        const formData = new FormData(this.form);
        const data = {
            LastName: formData.get('LastName'),
            Title: formData.get('Title'),
            Email: formData.get('Email'),
            Company: formData.get('Company'),
            Phone: formData.get('Phone'),
            Description: formData.get('Material'),
            LeadSource: 'book'
        };

        if (!data.LastName || data.LastName.toString().trim() === '') {
            alert('이름을 입력해주세요.');
            const lastNameInput = this.form.querySelector('input[name="LastName"]') as HTMLInputElement;
            lastNameInput.focus();
            return;
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 10000); // 10초 타임아웃

        try {
            const response = await fetch(this.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                    'Origin': window.location.origin
                },
                mode: 'cors',
                credentials: 'omit',
                body: JSON.stringify(data),
                signal: controller.signal
            });

            clearTimeout(timeoutId); // 타임아웃 취소

            const responseText = await response.text();

            if (response.ok) {
                try {
                    const result = JSON.parse(responseText);
                    if (result.message) {
                        // Material의 value(영문)로 파일명 만들기
                        const materialValue = formData.get('Material') as string || 'document';
                        const fileName = materialValue + '.pdf';

                        fetch(result.message)
                            .then(res => {
                                if (!res.ok) throw new Error('파일 다운로드 실패');
                                return res.blob();
                            })
                            .then(blob => {
                                const url = window.URL.createObjectURL(blob);
                                const a = document.createElement('a');
                                a.href = url;
                                a.download = fileName;
                                document.body.appendChild(a);
                                a.click();
                                a.remove();
                                window.URL.revokeObjectURL(url);
                                alert('자료가 성공적으로 다운로드되었습니다.');
                            })
                            .catch(() => {
                                alert('파일 다운로드에 실패했습니다.');
                            });
                    }
                } catch (parseError) {
                    console.error('Error parsing success response:', parseError);
                    throw new Error('서버 응답을 처리하는 중 오류가 발생했습니다.');
                }
            } else {
                let errorMessage = `서버 응답 오류 (${response.status})`;
                try {
                    if (!responseText) {
                        errorMessage = '서버에서 응답이 없습니다. 잠시 후 다시 시도해주세요.';
                    } else {
                        const errorData = JSON.parse(responseText);
                        console.error('Error response data:', errorData);
                        if (response.status === 400) {
                            errorMessage = errorData.message;
                            alert(errorMessage);
                            return;
                        } else {
                            errorMessage = errorData.message || errorMessage;
                        }
                    }
                } catch (parseError) {
                    console.error('Error parsing error response:', parseError);
                    errorMessage = '서버 응답을 처리하는 중 오류가 발생했습니다.';
                }
                throw new Error(errorMessage);
            }
        } catch (error) {
            clearTimeout(timeoutId); // 타임아웃 취소

            console.error('Detailed error:', {
                message: error instanceof Error ? error.message : '알 수 없는 에러',
                error: error,
                endpoint: this.endpoint,
                timestamp: new Date().toISOString()
            });

            let errorMessage = '서버 연결에 실패했습니다.';
            if (error instanceof TypeError) {
                if (error.message === 'Failed to fetch') {
                    errorMessage = '서버에 연결할 수 없습니다. 인터넷 연결을 확인해주세요.';
                } else if (error.name === 'AbortError') {
                    errorMessage = '서버 응답 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.';
                }
            }
            
            alert(errorMessage);
        }
    }
}

const LeadPage: React.FC = () => {
    const params = new URLSearchParams(window.location.search);
    const opt = params.get('opt');
    let materialValue = '';
    let materialLabel = '';
    if (opt === 'AgentforceGuide') {
        materialValue = 'AgentforceGuide';
        materialLabel = 'Agentforce 간편 개발 가이드';
    } else if (opt === 'AgentLite') {
        materialValue = 'AgentLite';
        materialLabel = 'AgentLite 경량화된 LLM 기반 작업 지향 에이전트 개발 라이브러리';
    }

    useEffect(() => {
        new FormSubmitter(
            'info', 
            'button[type="submit"]', 
            'https://90dhnop7pf.execute-api.ap-northeast-2.amazonaws.com/v1/lead/lead-form'
        );
    }, []);

    const onInputPhone = (e: React.ChangeEvent<HTMLInputElement>) => {
        let value = e.target.value.replace(/[^0-9]/g, '');
        if (value.length > 3 && value.length <= 7) {
            value = value.replace(/(\d{3})(\d{0,4})/, '$1-$2');
        } else if (value.length > 7) {
            value = value.replace(/(\d{3})(\d{4})(\d{0,4})/, '$1-$2-$3');
        }
        e.target.value = value;
    };

    return (
        <div className="lead-bg" style={{ backgroundImage: 'url("@lead_bg.jpg")', backgroundSize: 'cover', backgroundPosition: 'center' }}>
            <div className="lead-header">
                <a href="https://kusrc.co.kr/main" target="_blank" rel="noreferrer">
                    <img className="lead-logo" src="https://kusrc-static.s3.ap-northeast-2.amazonaws.com/static/img/kusrc_logo.svg" alt="KUSRC Logo" />
                </a>
                <div className="lead-title">
                    Salesforce Einstein으로 여는<br />
                    <span>AI 혁신 자료 다운로드</span>
                </div>
            </div>
            <div className="lead-card">
                <div className="content_box">
                    <h5>정보 작성</h5>
                    <form id="info">
                        <label>
                            <div className="title">
                                자료 <span className="required-star">*</span>
                            </div>
                            {/* 보여주기용 readonly input */}
                            <input type="text" value={materialLabel} readOnly className="readonly-material-input" />
                            {/* 실제 제출용 hidden input */}
                            <input type="hidden" name="Material" value={materialValue} />
                        </label>
                        <label>
                            <div className="title">
                                이름 <span className="required-star">*</span>
                            </div>
                            <input name="LastName" type="text" placeholder="이름을 입력해주세요" required />
                        </label>
                        <label>
                            <div className="title">
                                직함 <span className="required-star">*</span>
                            </div>
                            <input name="Title" type="text" placeholder="직함을 입력해주세요" required />
                        </label>
                        <label>
                            <div className="title">
                                이메일 <span className="required-star">*</span>
                            </div>
                            <input name="Email" type="email" placeholder="example@gmail.com" required />
                        </label>
                        <label>
                            <div className="title">
                                회사 <span className="required-star">*</span>
                            </div>
                            <input name="Company" type="text" placeholder="회사를 입력해주세요." required />
                        </label>
                        <label>
                            <div className="title">
                                전화번호 <span className="required-star">*</span>
                            </div>
                            <input 
                                name="Phone" 
                                type="text" 
                                onChange={onInputPhone}
                                maxLength={13} 
                                placeholder="010-1234-5678" 
                                required
                            />
                        </label>
                    </form>
                    <p className="policy">
                        등록시 개인정보 보호 정책에 따라
                        <a href="https://kusrc.co.kr/policy">KUSRC</a>가 귀하의 개인 데이터를
                        처리하는 것에 동의하는 것이 됩니다.
                    </p>
                </div>
                <div className="btn_wrapper">
                    <button type="submit" form="info">자료 다운로드</button>
                </div>
            </div>
        </div>
    );
};

export default LeadPage; 